import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInView } from "react-intersection-observer";

import { LedgerAccount } from "@/enums/ledgerAccounts";
import { useCustomerAccount } from "@/providers";
import { useAppDispatch } from "@/redux/hooks";
import {
  enhancedApi,
  useGetLedgerEntriesQuery,
} from "@/redux/services/enhancedApi";
import {
  type GetLedgerEntriesApiArg,
  TimeFrame,
} from "@generated/redux/services/generated/api";

// Module-scoped stable fallback to avoid identity churn in downstream memo deps
// See .claude/rules/web/render-stability.md Rule #1
const EMPTY_DATA: readonly LedgerEntryResolvedType[] = [];

type RefreshOptions = {
  delay?: number;
};

const LedgerEntryListContext = createContext<{
  data: LedgerEntryResolvedType[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isInitialLoad: boolean;
  isEmpty: boolean;
  /** True while loading page 2+ — not background refetch of page 1 / cache */
  isPaginating: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  loadMore: () => void;
  loadMoreRef: (node?: Element | null) => void;
  refetch: () => void;
  refresh: (options?: RefreshOptions) => void;
  filters: Partial<GetLedgerEntriesApiArg>;
  setFilters: (filters: Partial<GetLedgerEntriesApiArg>) => void;
  resetFilters: () => void;
  product?: string;
} | null>(null);

export type LedgerEntryListProviderProps = React.PropsWithChildren<{
  product?: string;
}>;

const PAGE_SIZE = 15;

/**
 * The `getLedgerEntries` args for a list. Shared with the boot prefetch in
 * `CustomerAccountProvider`: the endpoint's cache key is a `JSON.stringify` of
 * these args, so key order matters, and building them in one place keeps the
 * prefetched entry the one this provider reads.
 */
export const buildLedgerEntriesArgs = ({
  customerAccountId,
  product,
  filters,
  cursor,
}: {
  customerAccountId: string | undefined;
  product?: string;
  filters: Partial<GetLedgerEntriesApiArg>;
  cursor: string | undefined;
}): GetLedgerEntriesApiArg => ({
  ...filters,
  ...(product && {
    filterProduct: [product],
  }),
  // The query is skipped while the id is undefined.
  filterCustomerAccount: [customerAccountId as string],
  timeFrame: TimeFrame.Value,
  filterLedgerCode: [
    LedgerAccount.CustomerFunds,
    LedgerAccount.TradeReceivables,
    LedgerAccount.TradePayables,
  ],
  pageSize: PAGE_SIZE,
  pageCursor: cursor,
});

const LedgerEntryListProvider = ({
  children,
  product,
}: LedgerEntryListProviderProps) => {
  const { customerAccount } = useCustomerAccount();
  const dispatch = useAppDispatch();
  const [filters, setFilters] = useState<Partial<GetLedgerEntriesApiArg>>({});
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  // The committed cursor, for the delayed refetch in `refresh`.
  const cursorRef = useRef(cursor);
  // Bumped by `refetch`/`refresh`; the effect after the query hook turns each
  // bump into a refetch of the page-1 args.
  const [refetchRequest, setRefetchRequest] = useState(0);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const currentCustomerAccountId = customerAccount?.customerAccount?.id;
  const prevCustomerAccountIdRef = useRef<string | undefined>(
    currentCustomerAccountId
  );
  const prevFiltersKeyRef = useRef<string>("");

  // The account the cursor and filters were set under. On a switch they are
  // reset here, during render. The query hook below is still called once with
  // the new account's id and the old cursor and filters, but React throws that
  // render away before committing it, and the hook only subscribes (and so
  // requests) from an effect (RTK Query's default; this app never sets
  // `unstable__sideEffectsInRender`), so no request pairs the new account with
  // the old cursor or filters. Resetting them in an effect is too late: the
  // query hook's own effect, earlier in the same commit, has already requested
  // the new account with the old cursor.
  const [listCustomerAccountId, setListCustomerAccountId] = useState(
    currentCustomerAccountId
  );
  if (listCustomerAccountId !== currentCustomerAccountId) {
    setListCustomerAccountId(currentCustomerAccountId);
    if (listCustomerAccountId) {
      setCursor(undefined);
      setFilters({});
    }
  }

  const { ref: loadMoreRef, inView } = useInView({ rootMargin: "600px 0px" });

  const queryArgs: GetLedgerEntriesApiArg = useMemo(
    () =>
      buildLedgerEntriesArgs({
        customerAccountId: currentCustomerAccountId,
        product,
        filters,
        cursor,
      }),
    [cursor, filters, product, currentCustomerAccountId]
  );

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    isUninitialized,
    refetch: refetchQuery,
  } = useGetLedgerEntriesQuery(queryArgs, {
    skip: !customerAccount?.customerAccount?.id,
  });

  // Read by the refetch effect below, which must run once per request and
  // never because the query hook handed back a new value.
  const refetchQueryRef = useRef(refetchQuery);
  const isUninitializedRef = useRef(isUninitialized);
  useEffect(() => {
    refetchQueryRef.current = refetchQuery;
    isUninitializedRef.current = isUninitialized;
  }, [refetchQuery, isUninitialized]);

  // `getLedgerEntries` keeps every page under one cache entry and appends
  // whatever a cursor request returns, so a refetch made while the cursor
  // points at page N fetches page N and appends it again. `refetch`/`refresh`
  // reset the cursor first; this effect refetches only after the reset has
  // rendered, by which point the query hook above has switched to the page-1
  // args (when that switch has already started the page-1 request, RTK Query
  // drops this duplicate). It refetches directly rather than invalidating
  // `LedgerEntries`: RTK Query holds a tag invalidation until no request in
  // the whole api is pending (the price and balance pollers can keep one in
  // flight), then refetches with the entry's args at that moment, which are
  // the next page's if the list has paged on in the meantime.
  // The page-1 request is also dropped while another ledger request is in
  // flight, so call `refetch`/`refresh` only while `isFetching` is false.
  useEffect(() => {
    if (refetchRequest === 0 || isUninitializedRef.current) {
      return;
    }
    void refetchQueryRef.current();
  }, [refetchRequest]);

  // A layout effect, so a `refresh` timer that fires between a cursor commit
  // and that commit's passive effects already sees the new cursor.
  useLayoutEffect(() => {
    cursorRef.current = cursor;
  }, [cursor]);

  // Optimistic: show RTK cache immediately. Skeleton only when there is no
  // cached page yet (first visit). Background refetches update in place —
  // including date-filter clear back onto a warm unfiltered cache entry.
  const isInitialLoad = isLoading && data === undefined;
  const ledgerData =
    (data?.data as LedgerEntryResolvedType[] | undefined) ??
    (EMPTY_DATA as LedgerEntryResolvedType[]);
  const hasNextPage = !!data?.meta?.endCursor;
  const isPaginating = Boolean(cursor) && isFetching;
  const isEmpty = !isInitialLoad && !isFetching && ledgerData.length === 0;

  const resetFilters = useCallback(() => {
    setFilters({});
    setCursor(undefined);
  }, []);

  const fetchNextPage = useCallback(() => {
    if (data?.meta?.endCursor && !isFetching) {
      setCursor(data.meta.endCursor);
    }
  }, [data?.meta?.endCursor, isFetching]);

  // Preserve the alias contract — `loadMore` is the same stable ref as `fetchNextPage`
  const loadMore = fetchNextPage;

  const refetch = useCallback(() => {
    setCursor(undefined);
    setRefetchRequest((request) => request + 1);
  }, []);

  const refresh = useCallback(
    (options: RefreshOptions = {}) => {
      refetch();

      const { delay = 500 } = options;

      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }

      refreshTimeoutRef.current = setTimeout(() => {
        // Skipped once the list has paged on since the reset: the query then
        // holds a cursor, and refetching it would append that page again.
        if (!isUninitialized && cursorRef.current === undefined) {
          void refetchQuery();
        }
      }, delay);
    },
    [isUninitialized, refetch, refetchQuery]
  );

  const setFiltersStable = useCallback(
    (newFilters: Partial<GetLedgerEntriesApiArg>) => {
      setFilters(newFilters);
      setCursor(undefined);
    },
    []
  );

  // Auto-load more when scrolling
  useEffect(() => {
    if (inView && data?.meta?.endCursor && !isFetching) {
      fetchNextPage();
    }
  }, [inView, data?.meta?.endCursor, isFetching]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtersKey = JSON.stringify({ filters, product });
  useEffect(() => {
    if (prevFiltersKeyRef.current === filtersKey) {
      return;
    }

    prevFiltersKeyRef.current = filtersKey;

    const ledgerListElement = document.getElementById("ledger-entry-list");
    if (!ledgerListElement) {
      return;
    }

    const elementTop =
      ledgerListElement.getBoundingClientRect().top + window.scrollY;
    const currentScrollY = window.scrollY;

    if (currentScrollY > elementTop) {
      ledgerListElement.scrollIntoView({
        behavior: "instant",
      });
    }
  }, [filtersKey]);

  // The cursor and filters were already reset during the switch render above.
  useEffect(() => {
    const currentCustomerAccountId = customerAccount?.customerAccount?.id;

    if (
      prevCustomerAccountIdRef.current &&
      prevCustomerAccountIdRef.current !== currentCustomerAccountId
    ) {
      dispatch(enhancedApi.util.invalidateTags(["LedgerEntries"]));
    }

    prevCustomerAccountIdRef.current = currentCustomerAccountId;
  }, [customerAccount?.customerAccount?.id, dispatch]);

  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  const value = useMemo(
    () => ({
      data: ledgerData,
      isLoading,
      isFetching,
      isError,
      isInitialLoad,
      isEmpty,
      isPaginating,
      error,
      hasNextPage,
      fetchNextPage,
      loadMore,
      loadMoreRef,
      refetch,
      refresh,
      filters,
      setFilters: setFiltersStable,
      resetFilters,
      product,
    }),
    // `loadMore` is an alias of `fetchNextPage` (same useCallback ref) — it
    // shares identity with `fetchNextPage` so listing both is dead weight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      ledgerData,
      isLoading,
      isFetching,
      isError,
      isInitialLoad,
      isEmpty,
      isPaginating,
      error,
      hasNextPage,
      fetchNextPage,
      loadMoreRef,
      refetch,
      refresh,
      filters,
      setFiltersStable,
      resetFilters,
      product,
    ]
  );

  return (
    <LedgerEntryListContext.Provider value={value}>
      {children}
    </LedgerEntryListContext.Provider>
  );
};

export default LedgerEntryListProvider;

export const useLedgerEntryList = () => {
  const context = useContext(LedgerEntryListContext);
  if (!context) {
    throw new Error(
      "useLedgerEntryList must be used within LedgerEntryListProvider"
    );
  }
  return context;
};
